import { useCallback } from "react";
import { normalizePositionRow } from "../dashboard/Helpers";

export default function PositionAdminActions({
  positionPermissionModal,
  positionPermissionSelection,
  setPositionDraft,
  positionDraft,
  normalizePermissionKeys,
  setAdminError,
  adminFetch,
  apiBaseUrl,
  readApiJson,
  setPositionRows,
  setPositionEditDraft,
  positionEditDraft,
  setEditingPositionId,
  editingPositionId
}) {
  const getPositionPermissionWorkingSelection = useCallback((target, fallbackPermissions) => {
    if (positionPermissionModal?.target === target) {
      return positionPermissionSelection;
    }
    return fallbackPermissions;
  }, [positionPermissionModal?.target, positionPermissionSelection]);

  const resetPositionDraft = useCallback(() => {
    setPositionDraft({ name: "", key: "", department: "", permissionLevel: 0, permissions: [] });
  }, [setPositionDraft]);

  const createPosition = useCallback(async () => {
    const nextName = String(positionDraft.name || "").trim();
    const nextKey = String(positionDraft.key || "").trim();
    const nextDepartment = String(positionDraft.department || "").trim();
    const nextPermissionLevel = Number(positionDraft.permissionLevel || 0) || 0;
    const nextPermissions = normalizePermissionKeys(positionDraft.permissions);

    if (!nextName || !nextKey || !nextDepartment) {
      setAdminError("Please fill in all position fields");
      return;
    }

    try {
      const response = await adminFetch(`${apiBaseUrl}/admin/positions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code: nextKey,
          key: nextKey,
          name: nextName,
          department: nextDepartment,
          permissionLevel: nextPermissionLevel,
          permissions: nextPermissions
        })
      });
      const data = await readApiJson(response);
      if (!response.ok) {
        throw new Error(data.message || "Create position failed");
      }

      const normalized = normalizePositionRow(data?.position, Date.now());
      setPositionRows((prev) => [...prev, normalized]);
      setAdminError("");
      resetPositionDraft();
    } catch (err) {
      if (err.message !== "Session expired") {
        setAdminError(err.message || "Create position failed");
      }
    }
  }, [
    adminFetch,
    apiBaseUrl,
    normalizePermissionKeys,
    positionDraft.department,
    positionDraft.key,
    positionDraft.name,
    positionDraft.permissionLevel,
    positionDraft.permissions,
    readApiJson,
    resetPositionDraft,
    setAdminError,
    setPositionRows
  ]);

  const startEditPosition = useCallback((item) => {
    setEditingPositionId(item.id);
    setPositionEditDraft({
      name: String(item.name || ""),
      key: String(item.key || ""),
      department: String(item.department || ""),
      permissionLevel: Number(item.permissionLevel || 0) || 0,
      permissions: normalizePermissionKeys(item.permissions)
    });
  }, [normalizePermissionKeys, setEditingPositionId, setPositionEditDraft]);

  const savePosition = useCallback(async (id) => {
    const nextName = String(positionEditDraft.name || "").trim();
    const nextKey = String(positionEditDraft.key || "").trim();
    const nextDepartment = String(positionEditDraft.department || "").trim();
    const nextPermissionLevel = Number(positionEditDraft.permissionLevel || 0) || 0;
    const nextPermissions = normalizePermissionKeys(positionEditDraft.permissions);

    if (!nextName || !nextKey || !nextDepartment) {
      setAdminError("Please fill in all position fields");
      return;
    }

    try {
      const response = await adminFetch(`${apiBaseUrl}/admin/positions/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code: nextKey,
          key: nextKey,
          name: nextName,
          department: nextDepartment,
          permissionLevel: nextPermissionLevel,
          permissions: nextPermissions
        })
      });
      const data = await readApiJson(response);
      if (!response.ok) {
        throw new Error(data.message || "Update position failed");
      }

      const normalized = normalizePositionRow(data?.position, id);
      setPositionRows((prev) => prev.map((item) => (item.id === id ? normalized : item)));
      setEditingPositionId(0);
      setAdminError("");
    } catch (err) {
      if (err.message !== "Session expired") {
        setAdminError(err.message || "Update position failed");
      }
    }
  }, [
    adminFetch,
    apiBaseUrl,
    normalizePermissionKeys,
    positionEditDraft.department,
    positionEditDraft.key,
    positionEditDraft.name,
    positionEditDraft.permissionLevel,
    positionEditDraft.permissions,
    readApiJson,
    setAdminError,
    setEditingPositionId,
    setPositionRows
  ]);

  const deletePosition = useCallback(async (id) => {
    try {
      const response = await adminFetch(`${apiBaseUrl}/admin/positions/${id}`, {
        method: "DELETE"
      });
      const data = await readApiJson(response);
      if (!response.ok) {
        throw new Error(data.message || "Delete position failed");
      }

      setPositionRows((prev) => prev.filter((item) => item.id !== id));
      if (editingPositionId === id) {
        setEditingPositionId(0);
      }
      setAdminError("");
    } catch (err) {
      if (err.message !== "Session expired") {
        setAdminError(err.message || "Delete position failed");
      }
    }
  }, [adminFetch, apiBaseUrl, editingPositionId, readApiJson, setAdminError, setEditingPositionId, setPositionRows]);

  return {
    getPositionPermissionWorkingSelection,
    resetPositionDraft,
    createPosition,
    startEditPosition,
    savePosition,
    deletePosition
  };
}
